import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Provider,
  provideZonelessChangeDetection
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig
} from '@angular/router';
import { ToastrModule } from 'ngx-toastr';

import { APP_PROVIDERS } from '@app/app.providers';
import { APP_ROUTES } from '@app/app.routes';
import { ACCOUNTS_PROVIDERS } from '@accounts/accounts.providers';
import { AUTH_PROVIDERS, MSAL_INTERCEPTOR } from '@auth/auth.providers';
import { CORE_PROVIDERS } from '@core/core.providers';
import { CLIENT_INTERCEPTOR } from '@core/interceptors/client.interceptor';
import { UNAUTHORIZED_INTERCEPTOR } from '@core/interceptors/unauthorized.interceptor';
import { CLIENT_DETAILS_PROVIDERS } from '@client-details/client-details.providers';
import { CLIENT_PROVIDERS } from '@clients/clients.providers';
import { FEDERATION_PROVIDERS } from '@federations/federations.providers';
import { FILE_UPLOAD_PROVIDERS } from '@file-upload/file-upload.providers';
import { MODULES_PROVIDERS } from '@modules/modules.providers';
import { MY_PROFILE_PROVIDERS } from '@my-profile/my-profile.providers';
import { PROJECTS_PROVIDERS } from '@projects/projects.providers';
import { REGIONS_PROVIDERS } from '@regions/regions.providers';
import { REPORTS_PROVIDERS } from '@reports/reports.providers';
import { MATERIAL_PROVIDERS } from '@shared/material/material.providers';
import { AppInitializerService } from '@shared/services/app-initializer.service';
import { SVG_ICONS_PROVIDERS } from '@shared/svg-icons/svg-icons.providers';
import { SYSTEM_ADMINS_PROVIDERS } from '@system-admins/system-admins.providers';
import { ZENDESK_PROVIDERS } from '@zendesk/zendesk.providers';

interface Environment {
  production: boolean;
}

export function getAppProviders(
  environment: Environment
): (EnvironmentProviders | Provider)[] {
  if (environment.production) {
    enableProdMode();
  }

  return [
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      })
    ),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        tapToDismiss: true,
        enableHtml: true
      })
    ),
    provideAppInitializer(() => inject(AppInitializerService).init()),
    // ! Angular animations won't be long term
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    provideHttpClient(
      withInterceptors([
        MSAL_INTERCEPTOR,
        CLIENT_INTERCEPTOR,
        UNAUTHORIZED_INTERCEPTOR
      ]),
      withInterceptorsFromDi()
    ),
    ...APP_PROVIDERS,
    ...AUTH_PROVIDERS,
    ...MATERIAL_PROVIDERS,
    ...CORE_PROVIDERS,
    ...SVG_ICONS_PROVIDERS,

    ...ACCOUNTS_PROVIDERS,
    ...CLIENT_DETAILS_PROVIDERS,
    ...CLIENT_PROVIDERS,
    ...FEDERATION_PROVIDERS,
    ...FILE_UPLOAD_PROVIDERS,
    ...MODULES_PROVIDERS,
    ...MY_PROFILE_PROVIDERS,
    ...PROJECTS_PROVIDERS,
    ...REGIONS_PROVIDERS,
    ...REPORTS_PROVIDERS,
    ...SYSTEM_ADMINS_PROVIDERS,
    ...ZENDESK_PROVIDERS
  ];
}
